import "./Workers.scss";
import { useEffect, useState } from "react";
import {
  MenuItem,
  InputAdornment,
  TextField,
  useMediaQuery,
  Autocomplete,
  Tooltip,
} from "@mui/material";
import UserPassword from "./../../assets/user-password.svg";
import ShowUser from "./../../assets/show-user.svg";
import EditUser from "./../../assets/edit-user.svg";
import DeleteUser from "./../../assets/delete.svg";
import { useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import RefreshIcon from "@mui/icons-material/Refresh";
import { DataGrid } from "@mui/x-data-grid";
import { countriesInArabic, WorkerColumns } from "../../SourceData";
import MessageModals from "../../components/MessageModals/MessageModals";
import { ConfigProvider, DatePicker, Empty, message } from "antd";
import AddGif from "./../../assets/add-gif.gif";
import BlueButton from "../../components/BlueButton/BlueButton";
import SwipeableModals from "./SwipeableModals/SwipeableModals";
import SwitchButton from "../../components/SwitchButton/SwitchButton";
import api from "../../utils/api";
import ar_EG from "antd/es/locale/ar_EG"; // Arabic locale
import dayjs from "dayjs";
import "dayjs/locale/ar"; // Import Arabic locale for dayjs
import { usePermissions } from "./../../hooks/usePermissions";
import { toast } from "react-toastify";
const { RangePicker } = DatePicker;

const Workers = () => {
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:767px)");
  const isTablet = useMediaQuery("(min-width: 768px) and (max-width: 1024px)");
  const [currentModalView, setCurrentModalView] = useState("changePassword");
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = useState([null, null]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [showFilters, setShowFilters] = useState(!isMobile);
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedWorker, setSelectedWorker] = useState(null);

  dayjs.locale("ar");

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Users data state
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleOpen = (view, workerId = null) => {
    if (view === "changePassword" && !hasPermission(33)) {
      toast.error("ليس لديك هذه الصلاحية");
      return;
    } else if (view === "editUser" && !hasPermission(34)) {
      toast.error("ليس لديك هذه الصلاحية");
      return;
    }

    if (workerId) {
      setSelectedWorker(workers.find((worker) => worker._id === workerId));
    }
    setCurrentModalView(view);
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    setSelectedWorker(null);
  };

  // Handle date range change
  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    // Reset to first page when filters change
    setPage(1);
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setQuery(value);

    // Only update searchTerm if query is empty or has more than 2 characters
    if (value === "" || value.length > 2) {
      setSearchTerm(value);
      // Reset to first page when filters change
      setPage(1);
    }
  };

  // Handle country selection change
  const handleCountryChange = (event, newValue) => {
    setSelectedCountry(newValue);
    // Reset to first page when filters change
    setPage(1);
  };

  // Reset all filters
  const resetFilters = () => {
    setQuery("");
    setSearchTerm("");
    setDateRange([null, null]);
    setSelectedCountry(null);
    setPage(1);
  };

  // Handle worker activation toggle
  const handleActive = async (id) => {
    if (!hasPermission(32)) {
      toast.error("ليس لديك صلاحية تغير حالة المستخدم");
      return;
    }
    setLoading(true);
    try {
      await api.patch(`/technicians/${id}/toggleState`);
      fetchWorkers();
      message.success("تم تعديل حالة المستخدم بنجاح");
    } catch (error) {
      console.error("Error toggling user status:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOnline = async (id) => {
    if (!hasPermission(32)) {
      toast.error("ليس لديك صلاحية تغير حالة المستخدم");
      return;
    }
    setLoading(true);
    try {
      await api.patch(`/technicians/${id}/toggleOnline`);
      fetchWorkers();
      message.success("تم تعديل حالة المستخدم بنجاح");
    } catch (error) {
      console.error("Error toggling user status:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch users data
  const fetchWorkers = async () => {
    if (!hasPermission(31)) {
      toast.error("ليس لديك صلاحية رؤية العمال");
      return;
    }
    setLoading(true);
    try {
      let params = {
        page,
        limit: pageSize,
      };

      // Add search term if it's not empty
      if (searchTerm) {
        params.q = searchTerm;
      }

      // Add date range parameters if selected
      if (dateRange && dateRange[0] && dateRange[1]) {
        params.startDate = dateRange[0].format("YYYY-MM-DD");
        params.endDate = dateRange[1].format("YYYY-MM-DD");
      }

      // Add country if selected
      if (selectedCountry) {
        params.country = selectedCountry;
      }

      const response = await api.get("/technicians", { params });

      if (response.data.status === "success") {
        setWorkers(response.data.data);
        setTotalCount(response.data.totalCount);
      }
    } catch (error) {
      console.error("Error fetching workers:", error);
      // Implement error handling/notification here
    } finally {
      setLoading(false);
    }
  };

  // Effect to fetch users when dependencies change
  useEffect(() => {
    fetchWorkers();
  }, [page, pageSize, searchTerm, dateRange, selectedCountry]);

  // Effect to handle responsive filter display
  useEffect(() => {
    if (!isMobile) {
      setShowFilters(true);
    }
  }, [isMobile]);

  const actionColumn = [
    {
      field: "isOnline",
      headerName: "متصل",
      minWidth: 60,
      headerAlign: "center",
      align: "center",
      sortable: false,
      renderCell: (params) => {
        const isOnline = params?.row?.isOnline === true;
        return (
          <SwitchButton
            checked={isOnline}
            onClick={() => handleOnline(params.row._id)}
          />
        );
      },
    },
    {
      field: "isActive",
      headerName: "الحالة",
      minWidth: 60,
      headerAlign: "center",
      align: "center",
      sortable: false,
      renderCell: (params) => {
        const isActive = params?.row?.isActive === true;
        return (
          <SwitchButton
            checked={isActive}
            onClick={() => handleActive(params.row._id)}
          />
        );
      },
    },

    {
      field: "action",
      headerName: "",
      width: 300,
      headerAlign: "center",
      align: "center",
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <div className="cell-action">
            <div
              className="cell-btn"
              onClick={() => handleOpen("changePassword", params.row._id)}
            >
              <img src={UserPassword} alt="تغيير كلمة المرور" />
            </div>

            <div
              className="cell-btn"
              onClick={() => {
                if (hasPermission(35)) {
                  navigate(`/workers/${params.row._id}`);
                } else {
                  toast.error("ليس لديك صلاحية لعرض التفاصيل");
                }
              }}
            >
              <img src={ShowUser} alt="عرض" />
            </div>

            <div
              className="cell-btn"
              onClick={() => handleOpen("editUser", params.row._id)}
            >
              <img src={EditUser} alt="تعديل" />
            </div>

            <div
              className="cell-btn"
              onClick={() => {
                if (!hasPermission(36)) {
                  toast.error("ليس لديك هذه الصلاحية");
                  return;
                }
                setSelectedWorker(params.row);
                setOpenDelete(true);
              }}
            >
              <img src={DeleteUser} alt="حذف" />
            </div>
          </div>
        );
      },
    },
  ];

  return (
    <div className="users">
      <div className="special-header">
        <p className="title">
          العمال <span className="count-badge">{totalCount || 0}</span>
        </p>

        <div className="actions-container">
          <div className="search">
            <TextField
              id="outlined-search"
              type="search"
              value={query}
              placeholder="ابحث بالاسم او رقم الهاتف"
              onChange={handleSearchChange}
              sx={{
                width: isMobile ? "100%" : isTablet ? "300px" : "360px",
                borderRadius: "8px",
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            {!isMobile && (
              <ConfigProvider locale={ar_EG} direction="rtl">
                <RangePicker
                  format={"DD MMM"}
                  style={{
                    width: isMobile ? "100%" : isTablet ? "200px" : "300px",
                  }}
                  placeholder={["من", "الي"]}
                  className="range-picker"
                  onChange={handleDateRangeChange}
                  value={dateRange}
                  popupStyle={{ direction: "rtl" }}
                  inputReadOnly={true}
                />
              </ConfigProvider>
            )}

            {isMobile && (
              <Tooltip title="تصفية النتائج">
                <FilterListIcon
                  className="filter-icon"
                  onClick={() => setShowFilters(!showFilters)}
                />
              </Tooltip>
            )}
          </div>
          {hasPermission(30) && (
            <BlueButton
              text="اضافة عامل جديد"
              size={isMobile ? "full" : isTablet ? "fit" : "large"}
              variant="fill"
              onClick={() => handleOpen("createShop")}
              icon={<img src={AddGif} alt="" />}
            />
          )}
        </div>

        {showFilters && (
          <div className="filters-container">
            <Autocomplete
              id="country-select"
              sx={{
                width: isMobile ? "100%" : "200px",
              }}
              options={countriesInArabic}
              value={selectedCountry}
              onChange={handleCountryChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="البلد"
                  InputLabelProps={{
                    shrink: true,
                    sx: {
                      background: "white",
                      transformOrigin: "right top",
                      transform: "translate(14px, -9px) scale(0.75)",
                      right: 24,
                      left: "auto",
                      paddingLeft: "4px",
                      paddingRight: "4px",
                    },
                  }}
                />
              )}
              renderOption={(props, option) => (
                <MenuItem {...props}>{option}</MenuItem>
              )}
            />

            {isMobile && (
              <ConfigProvider locale={ar_EG} direction="rtl">
                <RangePicker
                  format={"DD MMM"}
                  style={{
                    width: "100%",
                  }}
                  placeholder={["من", "الي"]}
                  className="range-picker"
                  onChange={handleDateRangeChange}
                  value={dateRange}
                  popupStyle={{ direction: "rtl" }}
                  inputReadOnly={true}
                />
              </ConfigProvider>
            )}

            <Tooltip title="إعادة ضبط التصفية">
              <div className="reset-btn" onClick={resetFilters}>
                <RefreshIcon />
              </div>
            </Tooltip>
          </div>
        )}
      </div>

      <div className="container">
        {hasPermission(31) && (
          <DataGrid
            sx={{ backgroundColor: "white", width: "100%" }}
            autoHeight={true}
            columns={WorkerColumns.concat(actionColumn)}
            rows={workers.map((user, index) => ({
              ...user,
              id: user._id || index,
              index: (page - 1) * pageSize + index + 1,
            }))}
            disableVirtualization
            disableColumnFilter
            disableColumnMenu
            loading={loading}
            pagination
            paginationMode="server"
            rowCount={totalCount}
            initialState={{
              pagination: {
                paginationModel: { pageSize, page: page - 1 },
              },
            }}
            pageSizeOptions={[10, 25, 50]}
            onPaginationModelChange={(model) => {
              // Handle page change
              if (model.page !== page - 1) {
                setPage(model.page + 1);
              }

              // Handle page size change
              if (model.pageSize !== pageSize) {
                setPageSize(model.pageSize);
                setPage(1); // Reset to first page when changing page size
              }
            }}
            getRowClassName={(params) => "data-grid-row"}
            localeText={{
              MuiTablePagination: {
                labelRowsPerPage: "مستخدم في الصفحة",
              },
              noRowsLabel: "لا توجد بيانات",
            }}
            components={{
              NoRowsOverlay: () => (
                <div className="no-data-container">
                  <Empty description="لا توجد بيانات للعرض" />
                </div>
              ),
            }}
            rowHeight={80}
          />
        )}
      </div>

      <MessageModals
        open={openDelete}
        currentView="delete"
        route={selectedWorker ? `/admin/technician/${selectedWorker._id}` : ""}
        onDataChange={fetchWorkers}
        onClose={() => {
          setOpenDelete(false);
          setSelectedWorker(null);
        }}
      />

      <SwipeableModals
        open={isOpen}
        onClose={handleClose}
        onOpen={handleOpen}
        currentView={currentModalView}
        worker={selectedWorker}
        onSuccess={fetchWorkers}
      />
    </div>
  );
};

export default Workers;
